import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../hooks/useAuth';
import { Link } from 'react-router-dom';

interface RecommendSectionProps {
  category: 'artists' | 'events' | 'nodes' | 'market';
}

interface RecommendItem {
  id: string;
  name: string;
  image?: string;
  genre?: string;
  location?: string;
  price?: number;
  score?: number;
}

export function RecommendSection({ category }: RecommendSectionProps) {
  const { user } = useAuth();
  const [items, setItems] = useState<RecommendItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchRecommendations();
    } else {
      fetchDefaultRecommendations();
    }
  }, [category, user]);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);

      // ユーザーのお気に入りジャンルを取得
      const { data: profile } = await supabase
        .from('profiles')
        .select('favorite_genres')
        .eq('id', user?.id)
        .single();

      const favoriteGenres = profile?.favorite_genres || [];

      // カテゴリーに応じてレコメンデーションを取得
      let query;
      
      if (category === 'artists') {
        // お気に入りアーティストのジャンルを取得
        const { data: favArtists } = await supabase
          .from('favorite_artists')
          .select('artist_id')
          .eq('user_id', user?.id);

        const favArtistIds = favArtists?.map(f => f.artist_id) || [];

        // 同じジャンルのアーティストを推薦
        query = supabase
          .from('artist_layers')
          .select('id, artist_name, profile_image, artist_main_genres(genre_name)')
          .neq('id', favArtistIds.length > 0 ? favArtistIds[0] : '')
          .limit(6);

        const { data } = await query;
        
        setItems(data?.map(item => ({
          id: item.id,
          name: item.artist_name || 'Unknown Artist',
          image: item.profile_image || `https://public.readdy.ai/ai/img_res/6ef2d4d29fe65b4e10f5f2133bf53407.jpg${item.id}&orientation=squarish`,
          genre: (item as any).artist_main_genres?.[0]?.genre_name
        })) || []);

      } else if (category === 'events') {
        // 購入チケットのジャンルから推薦
        const { data: tickets } = await supabase
          .from('event_reservations')
          .select('event_id')
          .eq('user_id', user?.id);

        query = supabase
          .from('events')
          .select('id, title, image_url, event_main_genres(genre_name), prefecture')
          .gte('date', new Date().toISOString())
          .order('date', { ascending: true })
          .limit(6);

        const { data } = await query;
        
        setItems(data?.map(item => ({
          id: item.id,
          name: item.title || 'Unknown Event',
          image: item.image_url || `https://readdy.ai/api/search-image?query=cyberpunk%20music%20event%20concert%20stage%20neon%20lights%20futuristic%20atmosphere&width=600&height=400&seq=event-${item.id}&orientation=landscape`,
          genre: (item as any).event_main_genres?.[0]?.genre_name,
          location: item.prefecture
        })) || []);

      } else if (category === 'nodes') {
        // お気に入りショップのジャンルから推薦
        query = supabase
          .from('shop_layers')
          .select('id, shop_name, shop_image, shop_main_genres(genre_name), prefecture')
          .eq('is_published', true)
          .limit(6);

        const { data } = await query;
        
        setItems(data?.map(item => ({
          id: item.id,
          name: item.shop_name || 'Unknown Node',
          image: item.shop_image || `https://readdy.ai/api/search-image?query=cyberpunk%20music%20venue%20bar%20club%20neon%20interior%20futuristic%20atmosphere&width=600&height=400&seq=shop-${item.id}&orientation=landscape`,
          genre: (item as any).shop_main_genres?.[0]?.genre_name,
          location: item.prefecture
        })) || []);

      } else if (category === 'market') {
        // 購入履歴から推薦
        query = supabase
          .from('merchandise')
          .select('id, name, image_url, price')
          .eq('status', 'available')
          .limit(6);

        const { data } = await query;
        
        setItems(data?.map(item => ({
          id: item.id,
          name: item.name || 'Unknown Item',
          image: item.image_url || `https://readdy.ai/api/search-image?query=cyberpunk%20merchandise%20product%20neon%20glow%20minimal%20background%20futuristic&width=400&height=400&seq=merch-${item.id}&orientation=squarish`,
          price: item.price
        })) || []);
      }

    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultRecommendations = async () => {
    try {
      setLoading(true);

      // 未ログインユーザー向けのデフォルト推薦
      let query;
      
      if (category === 'artists') {
        query = supabase
          .from('artist_layers')
          .select('id, artist_name, profile_image')
          .limit(6);

        const { data } = await query;
        
        setItems(data?.map(item => ({
          id: item.id,
          name: item.artist_name || 'Unknown Artist',
          image: item.profile_image || `https://readdy.ai/api/search-image?query=abstract%20cyberpunk%20music%20artist%20neon%20glow%20dark%20background%20minimal&width=400&height=400&seq=artist-${item.id}&orientation=squarish`
        })) || []);

      } else if (category === 'events') {
        query = supabase
          .from('events')
          .select('id, title, image_url, prefecture')
          .gte('date', new Date().toISOString())
          .order('date', { ascending: true })
          .limit(6);

        const { data } = await query;
        
        setItems(data?.map(item => ({
          id: item.id,
          name: item.title || 'Unknown Event',
          image: item.image_url || `https://readdy.ai/api/search-image?query=cyberpunk%20music%20event%20concert%20stage%20neon%20lights%20futuristic%20atmosphere&width=600&height=400&seq=event-${item.id}&orientation=landscape`,
          location: item.prefecture
        })) || []);

      } else if (category === 'nodes') {
        query = supabase
          .from('shop_layers')
          .select('id, shop_name, shop_image, prefecture')
          .eq('is_published', true)
          .limit(6);

        const { data } = await query;
        
        setItems(data?.map(item => ({
          id: item.id,
          name: item.shop_name || 'Unknown Node',
          image: item.shop_image || `https://readdy.ai/api/search-image?query=cyberpunk%20music%20venue%20bar%20club%20neon%20interior%20futuristic%20atmosphere&width=600&height=400&seq=shop-${item.id}&orientation=landscape`,
          location: item.prefecture
        })) || []);

      } else if (category === 'market') {
        query = supabase
          .from('merchandise')
          .select('id, name, image_url, price')
          .eq('status', 'available')
          .limit(6);

        const { data } = await query;
        
        setItems(data?.map(item => ({
          id: item.id,
          name: item.name || 'Unknown Item',
          image: item.image_url || `https://readdy.ai/api/search-image?query=cyberpunk%20merchandise%20product%20neon%20glow%20minimal%20background%20futuristic&width=400&height=400&seq=merch-${item.id}&orientation=squarish`,
          price: item.price
        })) || []);
      }

    } catch (error) {
      console.error('Error fetching default recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryPath = (itemId: string) => {
    switch (category) {
      case 'artists': return `/artist/${itemId}`;
      case 'events': return `/event/${itemId}`;
      case 'nodes': return `/shop/${itemId}`;
      case 'market': return `/merchandise`;
      default: return '#';
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-zinc-800/50 rounded-2xl h-48 animate-pulse" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-2xl p-10 text-center bg-zinc-800/40 border border-zinc-700/50">
        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
          <i className="ri-inbox-line text-4xl text-zinc-600" />
        </div>
        <p className="text-zinc-500 text-sm tracking-wide">おすすめが見つかりませんでした</p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {items.map((item) => (
          <Link
            key={item.id}
            to={getCategoryPath(item.id)}
            className="group rounded-2xl overflow-hidden transition-all duration-200 cursor-pointer bg-zinc-800/40 border border-zinc-700/50 hover:bg-zinc-700/50 hover:border-zinc-600/60"
            style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}
          >
            <div className="aspect-square overflow-hidden">
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-3">
              <h4 className="text-xs font-bold text-zinc-100 truncate group-hover:text-cyan-300 transition-colors mb-1">
                {item.name}
              </h4>
              {item.genre && (
                <p className="text-[11px] text-zinc-500 truncate mb-1">{item.genre}</p>
              )}
              {item.location && (
                <p className="text-[11px] text-zinc-600 truncate">
                  <i className="ri-map-pin-line mr-1" />
                  {item.location}
                </p>
              )}
              {item.price !== undefined && (
                <p className="text-xs text-cyan-300 font-bold mt-1">¥{item.price.toLocaleString()}</p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}